<template>
  <div>
    <Navbar />
    <Breadcrumb />
    <div class="mx-auto container-xl mb-5">
      <!-- Status indicator -->
      <!-- Survey information form, status and action buttons -->
      <div class="">
        <div class="title-area">
          <h1 class="page-title h2 mb-2">Survey: <span :class="`${anon ? 'g-anon-lg' : ''}`">{{ survey_name }}</span>
          </h1>
          <div class="text-md">
            Group: <router-link :to="{ name: 'grouppagegroup', params: { id: $store.state.currentgroup_id } }">{{ group_name }}</router-link>
          </div>
        </div>
        <div class="d-flex border-section justify-content-between">
          <div class="table-responsive d-table w-100 table-no-padding-first mb-5">
            <h3 class="mt-4">Survey information</h3>
            <table class="table first-td-500 table-alternative">
              <tbody>
              <tr>
                <td>
                  {{ screentexts[72] ? screentexts[72].element_text : 'Survey name' }}
                </td>
                <td class="editable normal-input">
                  <input v-if="editable" v-model="survey.survey_name" :class="`form-control ${anon ? 'g-anon' : ''}`" />
                  <span v-else :class="`${anon ? 'g-anon' : ''}`">
                    {{ survey.survey_name }}
                  </span>
                </td>
              </tr>
              <tr>
                <td>
                  {{ screentexts[71] ? screentexts[72].element_text : 'Form type' }}
                </td>
                <td class="editable normal-input">
                  <span>{{ surveytype_name }}</span>
                </td>
              </tr>
              <tr>
                <td>
                  {{ screentexts[73] ? screentexts[73].element_text : 'Start date and time' }}
                </td>
                <td class="editable">
                  <div>
                    <div
                      v-if="editable && survey_status_id < 3"
                      class="d-flex"
                    >
                      <BaseDatePicker
                        id="start_date"
                        v-model="survey_start_date"
                        :min-datetime="new Date().toLocaleDateString('sv-SE')"
                        @update:model-value="saveStartDateTime"
                      />
                      <Datetime
                        id="survey_start_time"
                        v-model="survey_start_time"
                        type="time"
                        input-class="form-control"
                        :format="'HH:mm'"
                        :placeholder="screentexts[62] ? screentexts[62].element_text : 'Select sending time'"
                        @input="onStartTimeChange"
                      />
                    </div>
                    <span v-else>
                      {{ survey_start_date || 'Not set' }}
                      {{ survey_start_time || '' }}
                    </span>
                  </div>
                </td>
              </tr>
              <tr>
                <td>
                  {{ screentexts[74] ? screentexts[74].element_text : 'End date and time' }}
                </td>
                <td class="editable">
                  <BaseDatePicker
                    v-if="editable && survey_status_id < 4"
                    id="end_date"
                    v-model="survey_end_date"
                    type="date"
                    :min-datetime="new Date().toISOString()"
                    :timezone="'Europe/Stockholm'"
                    @update:model-value="saveEndDate"
                  />
                  <span v-else>
                    {{ this.survey_end_date > '1970-01-02' ? this.format_date(this.survey_end_date) : 'Not set' }}
                  </span>
                </td>
              </tr>
              <tr>
                <td>
                  {{ screentexts[75] ? screentexts[75].element_text : 'Language' }}
                </td>
                <td class="editable select-column">
                  <select v-if="editable" v-model="survey.survey_language_id" class="form-select">
                    <option v-for="option in language_options" :key="option.value" :value="option.value">{{ option.text }}</option>
                  </select>
                  <span v-else>
                    {{ language_name }}
                  </span>
                </td>
              </tr>
              <tr>
                <td>
                  {{ screentexts[76] ? screentexts[76].element_text : 'Norm data' }}
                </td>
                <td class="editable select-column">
                  <select v-if="editable" v-model="survey_normvalues_id" class="form-select">
                    <option v-for="option in norm_values_options" :key="option.value" :value="option.value">{{ option.text }}</option>
                  </select>
                  <span v-else>
                    {{ normvalues_country_name }}
                  </span>
                </td>
              </tr>
              <tr>
                <td>
                  {{ screentexts[77] ? screentexts[77].element_text : 'Reminder' }}
                </td>
                <td class="editable">
                  <div v-if="editable && Number(survey_status_id) <= 3 && canremind" class="form-check d-flex align-items-center">
                    <input
                      type="checkbox"
                      v-model="survey_reminder"
                      :true-value="1"
                      :false-value="0"
                      class="form-check-input position-static me-2"
                      id="survey_reminder_check"
                    />
                    <label class="form-check-label" for="survey_reminder_check">
                      {{ screentexts[77] ? screentexts[77].element_text : 'Reminder' }}
                    </label>
                  </div>
                  <span v-else>
                    {{ Number(survey_reminder) === 1 ? 'Yes' : 'No' }}
                  </span>
                </td>
              </tr>
              <tr>
                <td>
                  {{
                    screentexts[104]
                      ? screentexts[104].element_text
                      : "Reminder date"
                  }}
                </td>
                <td class="editable">
                  <div>
                    <div v-if="editable && survey_status_id <= 3 && canremind">
                      <BaseDatePicker
                        id="survey_reminder_date"
                        v-model="survey_reminder_date"
                        type="date"
                        :min-datetime="new Date(reminder_date_min).toISOString()"
                        :timezone="'Europe/Stockholm'"
                      />
                    </div>
                    <span v-else>{{ Number(survey_reminder) === 1 ? format_date(survey_reminder_date) : 'Not set' }}</span>
                  </div>
                </td>
              </tr>
              <tr>
                <td>
                  {{
                    screentexts[107]
                      ? screentexts[107].element_text
                      : "Reminder email"
                  }}
                </td>
                <td class="editable normal-input">
                  <input v-if="editable && survey_status_id <= 3 && canremind" v-model="survey_reminder_email" :class="`form-control ${anon ? 'g-anon' : ''}`"
                    :placeholder="screentexts[108]
                      ? screentexts[108].element_text
                      : 'Enter email address'
                      " />
                  <span v-else>{{ survey_reminder_email }}</span>
                </td>
              </tr>
              </tbody>
            </table>
            <button
              :disabled="saving"
              class="btn btn-secondary mt-3"
              @click="toggleEditable"
            >
              {{ editable ? 'Save' : 'Edit' }}
            </button>
          </div>
          <div class="text-center alert-section">
            <div class="mt-4">
              <div v-if="Number(survey_status_id) === 2" class="bg-warning alert">
                {{ screentexts[90] ? screentexts[90].element_text : "Scheduled: " }}
                {{ format_date(survey_start_date) }}
              </div>
              <div v-if="Number(survey_status_id) === 3" class="bg-secondary alert">
                {{
                  survey.survey_end_date > '1970-01-02' ?
                  `${screentexts[91] ? screentexts[91].element_text : "Active until: "} ${ format_date(survey_end_date) }` :
                  `Incorrect survey status`
                }}
              </div>
              <div   v-if="Number(survey_status_id) === 4" class="bg-primary alert">
                {{
                  screentexts[92] ? screentexts[92].element_text : "Completed"
                }}
              </div>
            </div>
            <div class="d-none d-xl-inline-block">
              <Chart
                :sub="completecount"
                :total="membercount"
              />
            </div>
            <br>
            <!-- Print paper forms, only if survey has paper -->
            <div v-show="Number(has_paper) === 1" class="col-12 text-center">
              <button
                class="btn btn-primary text-center col-12 col-sm-8 col-md-6 col-lg-5 col-xl-12 my-2 d-none d-lg-inline-block"
                @click.prevent="print_paperforms">
                {{
                  screentexts[113]
                    ? screentexts[113].element_text
                    : "Print all paper forms"
                }}
              </button>
              <br>
            </div>
            <!-- Status 1 = Draft -->
            <!-- Status 2 = Ready to launch -->
            <div v-if="survey_status_id < 3" class="col-12 mx-auto">
              <button class="btn btn-primary text-center col-12 col-sm-8 col-md-6 col-lg-5 col-xl-12 my-2"
                :disabled="survey_status_id < 2" @click.prevent="groupsurvey_launch">
                {{
                  screentexts[114]
                    ? screentexts[114].element_text
                    : "Launch survey now!"
                }}
              </button>
            </div>
            <!-- Status 3 = Active -->
            <div v-if="Number(survey_status_id) === 3 && completecount > 0" class="col-12 mx-auto">
              <button class="btn btn-danger text-center col-12 col-sm-8 col-md-6 col-lg-5 col-xl-12 my-2"
                @click.prevent="groupsurvey_close_ask">
                {{
                  screentexts[115]
                    ? screentexts[115].element_text
                    : "Close survey"
                }}
              </button>
              <br>
            </div>
            <!-- Status 4 = Completed -->
            <div v-if="Number(survey_status_id) === 4" class="col-12 mx-auto">
              <button class="btn btn-secondary text-center col-12 col-sm-8 col-md-6 col-lg-5 col-xl-12 my-2"
                @click.prevent="groupsurvey_reopen">
                {{
                  screentexts[116]
                    ? screentexts[116].element_text
                    : "Re-open survey"
                }}
              </button>
              <br>
              <button v-if="spotify === '0'"
                class="btn btn-primary text-center col-12 col-sm-8 col-md-6 col-lg-5 col-xl-12 my-2"
                @click.prevent="groupsurvey_report">
                {{
                  screentexts[117]
                    ? screentexts[117].element_text
                    : "Download report"
                }}
              </button>
              <button v-if="spotify === '1'"
                class="btn btn-primary text-center col-12 col-sm-8 col-md-6 col-lg-5 col-xl-12 my-2"
                @click.prevent="groupsurvey_report">
                Download Spotify report
              </button>
              <br>
            </div>
            <div v-if="asadmin" class="col-12 mx-auto">
              <button
                class="btn btn-danger text-center col-12 col-sm-8 col-md-6 col-lg-5 col-xl-12 my-2"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#collapse-1"
                aria-expanded="false"
                aria-controls="collapse-1">
                <fa-icon class="mr-4" icon="skull-crossbones" />
                Show admin functions
              </button>
            </div>
          </div>
        </div>
        <div v-if="asadmin" id="collapse-1" class="collapse border-0 mx-auto col-12 pl-0 px-0">
          <div class="card px-0 my-2 mx-auto col-12">
            <div class="card-body">
              <button v-if="Number(survey_type_id) === 1" class="btn btn-secondary mr-2 px-2" @click.prevent="admin_excluded">
                Set optional questions
              </button>
              <button class="btn btn-secondary mr-2 px-2" @click.prevent="admin_mailtext">
                Change mail subject/text
              </button>
              <button class="btn btn-secondary mr-2 px-2" @click.prevent="admin_reimport">
                Re-import answers
              </button>
              <button class="btn btn-secondary mr-2 px-2" @click.prevent="admin_addon">
                Add-on Survey
              </button>
              <div v-if="administer_excluded" class="py-4">
                <h5>Optional questions</h5>
                <div v-for="excludablequestion in excludablequestions" :key="excludablequestion.generalquestion_id"
                  class="row px-0 mx-0 py-0 my-0">
                  <div class="form-check">
                    <input
                      type="checkbox"
                      :id="excludablequestion.generalquestion_id"
                      v-model="excludedquestions"
                      :value="excludablequestion.generalquestion_id"
                      class="btn-check"
                    />
                    <label
                      :for="excludablequestion.generalquestion_id"
                      class="btn btn-outline-light text-dark">
                      <fa-icon v-if="!excludedquestions.includes(excludablequestion.generalquestion_id)" icon="times"
                        class="mr-1" />
                      <fa-icon v-if="excludedquestions.includes(excludablequestion.generalquestion_id)" icon="plus"
                        class="mr-1 mb-1 mt-0 pt-0" />
                      <span class="mt-1"
                        :class="excludedquestions.includes(excludablequestion.generalquestion_id) ? 'gdq-strike' : ''">
                        {{ excludablequestion.generalquestion_text }}
                      </span>
                    </label>
                  </div>
                </div>
                <button class="btn btn-primary" @click.prevent="admin_excluded_save">
                  Save optional questions
                </button>
              </div>
              <div v-if="administer_mailtext" class="py-4">
                <h5>Mailtexts</h5>
                <div class="mb-3">
                  <label class="form-label">Invitation E-mail Subject Line</label>
                  <input v-model="survey_invitation_subject" class="form-control col-12" />
                </div>
                <div class="mb-3">
                  <label class="form-label">Invitation E-mail Personal Text</label>
                  <BaseEditor v-model="survey_invitation_consultant_text" :editor-toolbar="editortoolbar" />
                </div>
                <button class="btn btn-primary" @click.prevent="admin_mailtext_save">
                  Save mail text
                </button>
              </div>
              <div v-if="administer_reimport" class="py-4">
                <div v-if="reimportstatus" :class="['alert', 'alert-dismissible', 'fade', 'show', Number(reimportstatus) === 2 ? 'alert-danger' : 'alert-success']" role="alert">
                  <span v-if="Number(reimportstatus) === 2">Data for import was not found</span>
                  <span v-if="Number(reimportstatus) === 1">Import done.</span>
                  <button type="button" class="btn-close" @click="reimportstatus = 0" aria-label="Close"></button>
                </div>
                <h5>Re-import answers</h5>
                This function will attempt to re-import the survey answers (GDQ and general) from the old system. You can
                run this function more than once for a survey without doing any damage.
                <div v-show="reimporting > 0" class="row mx-1">
                  <h5>
                    Re-importing {{ reimporting }} of {{ $store.state.currentsurveymembers.length }}
                  </h5>
                </div>
                <div v-show="Number(reimporting) === -1" class="row">
                  <button class="btn btn-primary mt-2 mx-3" @click.prevent="reimport">
                    Re-import answers
                  </button>
                  <button class="btn btn-danger mt-2 mx-3" @click.prevent="admin_reimport">
                    Cancel
                  </button>
                </div>
              </div>
              <div v-if="administer_addon" class="py-4">
                <select v-model="addon_survey" class="form-select"
                  @change="savefield('addon_survey', addon_survey)">
                  <option v-for="option in addonsurveys" :key="option.value" :value="option.value">{{ option.text }}</option>
                </select>
              </div>
            </div>
          </div>
        </div>
        <!-- /b-card -->
      </div>

      <div class="mt-5">
        <div>
          <div class="accordion full-width" role="tablist">
            <div class="card">
              <header role="tab" class="p-0">
                <button
                  class="btn btn-info btn-block text-left w-100"
                  id="scrollto"
                  type="button"
                  aria-expanded="true"
                  aria-controls="accordion-1"
                >
                  {{
                    screentexts[118]
                      ? screentexts[118].element_text
                      : "Survey participants"
                  }}
                </button>
              </header>
            </div>
            <div id="accordion-1" class="collapse show" role="tabpanel">
              <div class="card-body">
                  <div class="d-flex flex-wrap align-items-center gap-2 mb-2">
                    <button
                      v-show="survey_status_id < 4"
                      class="btn btn-primary"
                      @click.prevent="toggleaddmember"
                    >
                      {{
                        screentexts[119]
                          ? screentexts[119].element_text
                          : "Add survey participant"
                      }}
                    </button>
                    <!-- Contact members -->
                    <button
                      v-show="numberSelectedMembersWithEmail > 0"
                      :disabled="Number(selectedCount) === 0"
                      class="btn btn-secondary"
                      @click.prevent="togglecontactmembers"
                    >
                      {{
                        screentexts[120] ? screentexts[120].element_text : "Contact"
                      }}
                      {{ numberSelectedMembersWithEmail }}
                      {{
                        screentexts[121] ? screentexts[121].element_text : "member"
                      }}<span v-if="
                        numberSelectedMembersWithEmail > 1 || Number(numberSelectedMembersWithEmail) === 0
                      ">s</span>
                    </button>
                    <button
                      v-show="survey_status_id < 4 && numberSelectedMembersWithEmail > 0"
                      :disabled="Number(selectedCount) === 0"
                      class="btn btn-warning"
                      @click.prevent="togglesendinvitations"
                    >
                      {{
                        screentexts[122] ? screentexts[122].element_text : "Send"
                      }}
                      {{ numberSelectedMembersWithEmail }}
                      {{
                        screentexts[123]
                          ? screentexts[123].element_text
                          : "invitation"
                      }}<span v-if="
                        numberSelectedMembersWithEmail > 1 || Number(numberSelectedMembersWithEmail) === 0
                      ">s</span>
                    </button>
                  </div>
                  <div v-if="invitationsSent > 0 && numberSelectedMembersWithEmail === 0" class="alert alert-success mt-2 mb-0 py-2">
                    {{ invitationsSent }} invitation<span v-if="invitationsSent > 1">s</span> sent successfully.
                  </div>
                  <div v-if="addmember" class="mt-3">
                    <h4 class="mb-3">
                      {{
                        screentexts[119]
                          ? screentexts[119].element_text
                          : "Add survey participant"
                      }}
                    </h4>
                    <div class="bg-secondary add-member-container p-3">
                    <form novalidate @submit.stop.prevent>
                      <div class="mb-3">
                        <label class="mb-2 d-block">
                          {{ screentexts[167] ? screentexts[167].element_text : 'Form Type' }}
                        </label>
                        <select v-model="groupmember_formoption_id" class="form-select w-100" @change="groupmember_email_valid = null">
                          <option v-for="option in formoptions" :key="option.value" :value="option.value">{{ option.text }}</option>
                        </select>
                      </div>
                      <div v-show="Number(groupmember_formoption_id) === 1" class="mb-3">
                        <label class="mb-2 d-block">
                          {{ screentexts[164] ? screentexts[164].element_text : 'Email address' }}
                        </label>
                        <input v-model="groupmember_email" class="form-control w-100" :class="[anon ? 'g-anon' : '', groupmember_email_valid === 'invalid' ? 'is-invalid' : '']"
                          type="text"
                          :placeholder="screentexts[170] ? screentexts[170].element_text : 'Email Address'">
                        <div v-if="groupmember_email_valid === 'invalid'" class="invalid-feedback d-block">
                          Invalid or duplicate email address
                        </div>
                      </div>
                      <div class="mb-0 mt-4">
                        <div class="d-flex justify-content-end gap-2">
                          <button class="btn btn-primary" @click.prevent="addgroupmember">
                            Add
                          </button>
                          <button class="btn btn-danger" @click.prevent="toggleaddmember">
                            Cancel
                          </button>
                        </div>
                      </div>
                    </form>
                    </div>
                  </div>
                  <div v-show="contactmembers === true" class="mt-3 bg-secondary add-member-container p-3">
                <h4>
                  {{
                    screentexts[124]
                      ? screentexts[124].element_text
                      : "Contact Members"
                  }}
                  {{
                    screentexts[129]
                      ? screentexts[129].element_text
                      : "email"
                  }}<span v-if="numberSelectedMembersWithEmail > 1">s</span>
                </h4>
                        <p v-if="numberSelectedMembersWithEmail != selectedCount" class="mb-3">
                  Please note: Some selected members do not have an email address and will not be contacted using this function
                </p>
                <form novalidate @submit.stop.prevent>
                  <div class="mb-3">
                    <label class="mb-2">
                      {{ screentexts[131]
                        ? screentexts[131].element_text
                        : 'Email subject' }}
                    </label>
                    <input v-model="mail_subject" :placeholder="screentexts[125]
                      ? screentexts[125].element_text
                      : 'Email subject'
                      " class="form-control w-100" />
                  </div>
                  <div class="mb-3">
                    <label class="mb-2">
                      {{ screentexts[126]
                        ? screentexts[126].element_text
                        : 'Email text' }}
                    </label>
                    <BaseEditor v-model="mail_consultant_text" :editor-toolbar="editortoolbar" />
                  </div>
                  <div class="text-right mb-0">
                    <button class="btn btn-danger mr-3" @click.prevent="togglecontactmembers">
                      {{
                        screentexts[127]
                          ? screentexts[127].element_text
                          : "Cancel"
                      }}
                    </button>
                    <button class="btn btn-primary" @click.prevent="sendemails('Other')">
                      {{
                        screentexts[128]
                          ? screentexts[128].element_text
                          : "Send"
                      }}
                      {{ selectedCount }}
                      {{
                        screentexts[129]
                          ? screentexts[129].element_text
                          : "email"
                      }}<span v-if="selectedCount > 1">s</span>
                    </button>
                  </div>
                </form>
              </div>
              <div v-show="sendinvitations === true" class="mt-3 bg-secondary add-member-container p-3">
                <h4>
                  {{
                    screentexts[130]
                      ? screentexts[130].element_text
                      : "Send Invitations"
                  }}
                </h4>
              <p v-if="numberSelectedMembersWithEmail != selectedCount" class="mb-3">
                  Please note: Some selected members do not have an email address and will not be sent the invitation using this function
                </p>
                <form novalidate @submit.stop.prevent>
                  <div class="mb-3">
                    <label class="mb-2">
                      {{ screentexts[131]
                        ? screentexts[131].element_text
                        : 'Email subject' }}
                    </label>
                    <input v-model="survey_invitation_subject" :placeholder="screentexts[131]
                      ? screentexts[131].element_text
                      : 'Email subject'
                      " class="form-control w-100" @change="
                        savefield(
                          'survey_invitation_subject',
                          survey_invitation_subject
                        )
                        " />
                  </div>
                  <div v-if="survey.spotify != 1" class="mb-3">
                    <label class="mb-2">
                      {{ screentexts[155] ? screentexts[155].element_text : 'Invitation e-mail fixed text' }}
                    </label>
                    <div class="text-container">
                      <div id="fixed-text" class="mt-3"
                        v-html="this.$store.state.currentsurvey.survey_invitation_text" />
                      <div class="d-flex justify-content-end mb-4">
                        <button class="btn btn-primary btn-tinyall" @click="copyText">
                          Copy Text
                        </button>
                      </div>
                    </div>
                  </div>
                  <div class="mb-3">
                    <label class="mb-2">
                      <span>{{
                        screentexts[17] ? screentexts[17].element_text : ""
                        }}</span>
                      <!-- <PopupBubble
                        v-if="screentexts[17] && screentexts[17].element_bubbletext != '' && screentexts[17].element_bubbletext != null"
                        element_id="invitation-email-personal-text"
                        :screen_id="screen_id"
                        screenelement_id="17"
                        :bubbletext="
                          screentexts[17]
                            ? screentexts[17].element_bubbletext
                            : ''
                        "
                      /> -->
                    </label>
                    <BaseEditor id="survey_invitation_consultant_text" v-model="survey_invitation_consultant_text"
                      :editor-toolbar="editortoolbar" @blur="
                        savefield(
                          'survey_invitation_consultant_text',
                          survey_invitation_consultant_text
                        )
                        " />
                  </div>
                  <div class="mb-3">
                    <label class="mb-2">
                      {{ screentexts[132]
                        ? screentexts[132].element_text
                        : 'Email replies to' }}
                    </label>
                    <input v-model="survey_invitation_cc" :placeholder="screentexts[133]
                      ? screentexts[133].element_text
                      : 'Email reply address'
                      " class="form-control w-100" @change="
                        savefield('survey_invitation_cc', survey_invitation_cc)
                        " />
                  </div>
                  <div class="text-right mb-0">
                    <button class="btn btn-danger mr-3" @click.prevent="togglesendinvitations">
                      {{
                        screentexts[134]
                          ? screentexts[134].element_text
                          : "Cancel"
                      }}
                    </button>
                    <button class="btn btn-primary" @click.prevent="sendemails('Invitation')">
                      {{
                        screentexts[135]
                          ? screentexts[135].element_text
                          : "Send"
                      }}
                      {{ selectedCount }}
                      {{
                        screentexts[136]
                          ? screentexts[136].element_text
                          : "Invitation"
                      }}<span v-if="selectedCount > 1">s</span>
                    </button>
                  </div>
                </form>
              </div>
              <BaseTable
                :fields="fields"
                :class="'actions-table save-cancel'"
                :items="includedGroupMembers"
                :tbody-tr-class="rowClass"
                hover
              >
                <template v-slot:head(select)>
                  <input
                    type="checkbox"
                    v-model="selected"
                    @click.stop
                    @change="onSelectAllChange"
                    class="form-check-input"
                  />
                </template>
                <template v-slot:cell(select)="{ data }">
                  <div class="d-flex justify-content-center align-items-center">
                    <input
                      v-if="isIncluded(data.item)"
                      type="checkbox"
                      v-model="data.item.selected"
                      @click.stop
                      @change="onRowSelectChange"
                      class="form-check-input m-0"
                    />
                  </div>
                </template>
                <template v-slot:cell(actions)="{ data }">
                  <div class="d-flex justify-content-end align-items-center">
                    <div v-if="data.item.editable" class="d-flex">
                      <button class="btn btn-danger btn-tiny mr-1" @click.prevent="cancelEdit(data.item.id)">Cancel</button>
                      <button class="btn btn-primary btn-tiny" @click.prevent="saveEdit(data.item.id)">Save</button>
                    </div>
                    <div v-else class="ellipsis-button">
                      <BaseDropdown right variant="link">
                        <template #button-content>
                          <i class="fa-solid fa-ellipsis"></i>
                        </template>
                        <li><a class="dropdown-item" href="#" @click.prevent="editGroupMember(data.item.id)">Edit</a></li>
                        <li v-if="canExcludeMembers"><a class="dropdown-item" href="#" @click.prevent="togglegroupmember(data.item.id)">Exclude from this survey</a></li>
                        <li v-if="data.item.groupsurveymember_formoption_id != 2"><a class="dropdown-item" href="#" @click.prevent="getlink(data.item.id)">Copy invitation</a></li>
                        <li v-if="Number(data.item.groupsurveymember_formoption_id) === 2"><a class="dropdown-item" href="#" @click.prevent="printform(data.item.id)">Print form</a></li>
                        <li v-if="Number(data.item.groupsurveymember_formoption_id) === 2"><a class="dropdown-item" href="#" @click.prevent="openquestionnaire(data.item.id)">Open survey</a></li>
                        <li v-if="Number(data.item.groupsurveymember_formoption_id) === 1 && survey.survey_status_id < 4"><a class="dropdown-item" href="#" @click.prevent="toggleSelected(data.item.id, 'scrollto', 'invite')">Send invitation</a></li>
                        <li v-if="Number(data.item.groupsurveymember_formoption_id) === 1"><a class="dropdown-item" href="#" @click.prevent="toggleSelected(data.item.id, 'scrollto', 'contact')">Contact member</a></li>
                      </BaseDropdown>
                    </div>
                  </div>
                </template>

                <template v-slot:cell(groupsurveymember_status_id)="{ data }">
                  <div v-if="isIncluded(data.item)" class="d-flex align-items-center">
                    <span :class="statusBadgeClass(data.item)">
                      {{ statusLabel(data.item) }}
                    </span>
                  </div>
                  <div v-else class="text-muted small">Not participating</div>
                </template>

                <template v-slot:cell(groupsurveymember_survey_code)="{ data }">
                  {{ data.item.groupsurveymember_survey_code }}
                </template>

                <template v-slot:cell(groupmember_email)="{ data }">
                  <div class="position-relative">
                    <div class="uneditable" v-if="!data.item.editable" :class="{ 'g-anon': anon }">{{ data.item.groupmember_email }}</div>
                    <input
                        v-else
                        v-model="data.item.groupmember_email"
                        type="text"
                        class="form-control"
                        :class="{'g-anon': anon, 'text-danger': data.item.groupmember_email_valid === 'invalid'}"
                      />
                      <i
                        v-if="data.item.groupmember_email_valid === 'invalid' && data.item.editable"
                        class="fa-solid inline-tooltip fa-circle-question tooltip-question tooltip-danger"
                        title="Invalid email address"
                      ></i>
                  </div>
                </template>

                <!-- <template v-slot:cell(groupmember_name)="{ data }">
            <input
              v-model="data.item.groupmember_name"
              type="text"
              class="form-control"
            />
          </template> -->
                <template v-slot:cell(groupmember_mobile)="{ data }">
                  <input v-model="data.item.groupmember_mobile" type="text" class="form-control" />
                </template>

                <template v-slot:cell(groupsurveymember_formoption_id)="{ data }">
                  <div class="uneditable" v-if="!data.item.editable">
                    {{ getFormOptionText(data.item.groupsurveymember_formoption_id) }}
                  </div>
                  <select
                    v-else-if="Number(data.item.groupsurveymember_status_id) === 1"
                    v-model="data.item.groupsurveymember_formoption_id"
                    class="form-select"
                  >
                    <option v-for="option in formoptions" :key="option.value" :value="option.value">
                      {{ option.text }}
                    </option>
                  </select>
                  <div class="uneditable" v-else>
                    {{ getFormOptionText(data.item.groupsurveymember_formoption_id) }}
                  </div>
                </template>

              </BaseTable>
              <!-- <GroupSurveyMembers mode="edit" /> -->
              <h3 class="mt-4">
                Members not included in survey
              </h3>
              <BaseTable
                :fields="fieldsExclude"
                :class="'actions-table save-cancel excluded mb-5'"
                :items="excludedGroupMembers"
                :tbody-tr-class="rowClass"
                hover
              >
                <template v-slot:cell(actions)="{ data }">
                  <div class="d-flex justify-content-end align-items-center">
                    <div v-if="data.item.editable" class="d-flex">
                      <button class="btn btn-danger btn-tiny mr-1" @click.prevent="cancelEdit(data.item.id)">Cancel</button>
                      <button class="btn btn-primary btn-tiny" @click.prevent="saveEdit(data.item.id)">Save</button>
                    </div>
                    <div v-else class="ellipsis-button">
                      <BaseDropdown right variant="link">
                        <template #button-content>
                          <i class="fa-solid fa-ellipsis"></i>
                        </template>
                        <li><a class="dropdown-item" href="#" @click.prevent="editGroupMember(data.item.id)">Edit</a></li>
                        <li v-if="canExcludeMembers || data.item.groupsurveymember_status_id < 4"><a class="dropdown-item" href="#" @click.prevent="togglegroupmember(data.item.id)">Include in this survey</a></li>
                      </BaseDropdown>
                    </div>
                  </div>
                </template>

                <template v-slot:cell(groupmember_code)="{ data }">
                  {{ data.item.groupmember_code }}
                </template>

                <template v-slot:cell(groupmember_email)="{ data }">
                  <div>
                    <div class="uneditable" v-if="!data.item.editable" :class="{ 'g-anon': anon }">{{
                      data.item.groupmember_email }}</div>
                    <input v-else v-model="data.item.groupmember_email" type="text" class="form-control"
                      :class="{ 'g-anon': anon }" />
                  </div>
                </template>

                <template v-slot:cell(groupmember_mobile)="{ data }">
                  <input v-model="data.item.groupmember_mobile" type="text" class="form-control" />
                </template>

                <template v-slot:cell(groupsurveymember_formoption_id)="{ data }">
                  <div class="uneditable" v-if="!data.item.editable">{{
                    getFormOptionText(data.item.groupsurveymember_formoption_id) }}</div>
                  <select v-else-if="Number(data.item.groupsurveymember_status_id) === 1"
                    v-model="data.item.groupsurveymember_formoption_id" class="form-select">
                    <option v-for="option in formoptions" :key="option.value" :value="option.value">{{ option.text }}</option>
                  </select>
                  <div class="uneditable" v-else>{{
                    getFormOptionText(data.item.groupsurveymember_formoption_id) }}</div>
                </template>

              </BaseTable>
              </div>
            </div>
          </div>
          <div class="accordion full-width" role="tablist">
            <div class="card">
              <header class="p-0" role="tab">
                <button
                  class="btn btn-info btn-block text-left w-100 collapsed"
                  type="button"
                  data-bs-toggle="collapse"
                  data-bs-target="#accordion-2"
                  aria-expanded="false"
                  aria-controls="accordion-2"
                >
                  Scheduled emails
                </button>
              </header>
            </div>
            <div id="accordion-2" class="collapse" role="tabpanel">
              <div class="card-body">
                <GroupSurveyScheduledMails :surveyid="survey_id" :changed="surveychanged" status="scheduled" />
              </div>
            </div>
          </div>
          <div class="accordion full-width" role="tablist">
            <div class="card">
              <header class="p-0" role="tab">
                <button
                  class="btn btn-info btn-block text-left w-100 collapsed"
                  type="button"
                  data-bs-toggle="collapse"
                  data-bs-target="#accordion-3"
                  aria-expanded="false"
                  aria-controls="accordion-3"
                >
                  Sent emails
                </button>
              </header>
            </div>
            <div id="accordion-3" class="collapse" role="tabpanel">
              <div class="card-body">
                <GroupSurveyScheduledMails :surveyid="survey_id" :changed="surveychanged" status="sent" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <BaseModal v-model="wanttoclose" title="Close survey?">
      <template #default>
        <p>When you close the survey, participants can no longer fill in answers, and you can download the survey report</p>
        <div v-if="Number(anonymize_survey_on_close) === 1">
          <p></p>
          <p class="text-danger"><strong>All personal information in the survey will be anonymized. This cannot be undone.</strong></p>
          <div class="form-check mt-2">
            <input
              id="confirmAnonymizeCheck"
              v-model="confirmAnonymize"
              class="form-check-input"
              type="checkbox"
            >
            <label class="form-check-label" for="confirmAnonymizeCheck">
              I understand that all personal data will be permanently deleted
            </label>
          </div>
        </div>
      </template>
      <template #modal-footer>
        <button
          type="button"
          class="btn mt-3 w-100"
          :class="Number(anonymize_survey_on_close) === 1 ? 'btn-danger' : 'btn-primary'"
          :disabled="Number(anonymize_survey_on_close) === 1 && !confirmAnonymize"
          @click="groupsurvey_close"
        >
          Yes, close
        </button>
        <button type="button" class="btn btn-secondary mt-2 w-100" @click="groupsurvey_close_cancel">
          Cancel
        </button>
      </template>
    </BaseModal>
    <BaseModal
      v-model="showMemberLink"
      title="Questionnaire link and personal code"
      size="lg"
      :ok-only="true"
      ok-title="Close"
      cancel-title="Close"
    >
      <div class="mb-3">
        <textarea v-model="currentcodelink" class="form-control" rows="18" readonly></textarea>
      </div>
    </BaseModal>
    <BaseModal v-model="addingmember" :show-header="false" :show-footer="false" centered :closeable="false">
      <template #body>
        <div class="text-center p-4">
          <div class="spinner-border text-primary mb-3" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <div>Adding member...</div>
        </div>
      </template>
    </BaseModal>
  </div>
</template>
<script>
import Navbar from './Navbar.vue'
import Breadcrumb from './Breadcrumb.vue'
// import GroupSurveyLog from "./GroupSurveyLog.vue";
// import PopupBubble from "./PopupBubble.vue";
import { config } from '../../config'
import GroupSurveyScheduledMails from './GroupSurveyScheduledMails.vue'
import BaseTable from './base/BaseTable.vue'
import BaseDatePicker from './base/BaseDatePicker.vue'
import Datetime from './custom/Datetime.vue'
import BaseEditor from './base/BaseEditor.vue'
import BaseDropdown from './base/BaseDropdown.vue'
import Chart from './styleguide/Chart.vue'

const editortools = [
  // [{ font: [] }],
  [{ header: [false, 1, 2, 3, 4, 5, 6] }],
  //  [{ size: ["small", false, "large", "huge"] }],
  //  ["bold", "italic", "underline", "strike"],
  ['bold', 'italic'],
  [
    { align: '' },
    { align: 'center' },
    { align: 'right' },
    { align: 'justify' }
  ],
  //  [{ header: 1 }, { header: 2 }],
  //  ["blockquote", "code-block"],
  [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }],
  //  [{ script: "sub" }, { script: "super" }],
  //  [{ indent: "-1" }, { indent: "+1" }],
  //  [{ color: [] }, { background: [] }],
  [{ color: [] }],
  //  ["link", "image", "video", "formula"],
  ['image']
  //  [{ direction: "rtl" }],
  //  ["clean"]
]

export default {
name: 'GroupSurveyPage',
  components: {
    Navbar,
    Breadcrumb,
    //    GroupSurveyLog,
    Chart,
    GroupSurveyScheduledMails,
    BaseTable,
    BaseDatePicker,
    Datetime,
    BaseEditor,
    BaseDropdown
    // PopupBubble
  },
  data () {
    return {
      addingmember: false,
      isAddMemberVisible: false,
      loading: true,
      saving: false,
      editortoolbar: editortools,
      group_name: this.$store.state.currentgroup.group_name,
      surveytype_name: 'GDQ',
      survey: this.$store.state.currentsurvey,
      survey_name: null,
      survey_name_valid: null,
      survey_type_id: null,
      survey_status_id: null,
      survey_status_id_valid: null,
      survey_invoice_reference: null,
      survey_start_date: null,
      survey_start_date_valid: null,
      survey_start_time: null,
      survey_end_date: null,
      survey_end_date_valid: null,
      survey_end_time: null,
      survey_reminder: 0,
      survey_reminder_date: null,
      survey_reminder_date_valid: null,
      survey_reminder_time: null,
      survey_reminder_time_valid: null,
      survey_reminder_email: null,
      survey_reminder_email_valid: null,
      survey_invitation_subject: null,
      survey_invitation_text: this.$store.state.currentsurvey.survey_invitation_text,
      survey_invitation_consultant_text: this.$store.state.currentsurvey.survey_invitation_consultant_text,
      survey_invitation_cc: null,
      addon_survey: 0,
      addmember: false,
      contactmembers: false,
      sendinvitations: false,
      invitationsSent: 0,
      groupmember_name: '',
      groupmember_name_valid: null,
      groupmember_email: '',
      groupmember_email_valid: null,
      groupmember_mobile: '',
      groupmember_mobile_valid: null,
      groupmember_formoption_id: 1,
      groupsurveymember_formoption_id: 1,
      groupmembers_grid: [],
      nextid: 10000000,
      formchanged: false,
      mail_subject: null,
      mail_consultant_text: '',
      mail_cc: null,
      mail_text: this.$store.state.currentsurvey.survey_invitation_text,
      labels: [],
      addinglabel: 0,
      addinglabeltext: '',
      has_online: 0,
      has_paper: 0,
      screen_id: 7,
      screentexts: [],
      asadmin: this.$store.state.asadmin,
      administer_excluded: false,
      excludablequestions: [],
      excludedquestions: [],
      administer_mailtext: false,
      administer_reimport: false,
      administer_addon: false,
      language_options: [],
      norm_values_options: [],
      surveychanged: false,
      reimporting: -1,
      reimported: [],
      reimportstatus: 0,
      addonsurveys: [],
      anonymize_survey_on_close: this.$store.state.profile.anonymize_survey_on_close,
      wanttoclose: false,
      confirmAnonymize: false,
      editable: false,
      languages: [],
      profile: this.$store.state.profile,
      spotify: this.$store.state.currentsurvey.spotify,
      survey_normvalues_id: this.$store.state.currentsurvey.survey_normvalues_id,
      surveytype_options: [],
      survey_language_id: this.$store.state.currentsurvey.survey_language_id,
      normvalues_countries: this.$store.state.normvalues_countries,
      currentcode: '',
      currentlink: '',
      showMemberLink: false,
      selected: false,
      selectedCount: 0,
      fields: [
        { key: 'select', label: '', sortable: false, class: 'select-td', tdClass: 'editable select-td', thClass: 'select-th' },
        { key: 'groupsurveymember_survey_code', sortable: true, label: 'Code', tdClass: 'd-none d-sm-table-cell', thClass: 'd-none d-sm-table-cell' },
        { key: 'groupmember_email', label: 'Email', sortable: true, class: 'editable email-column' },
        { key: 'groupsurveymember_status_id', label: 'Status', sortable: true, class: 'editable color-input', tdClass: 'p-0', thClass: 'p-0' },
        // { key: 'groupmember_name', label: '[Member] name', tdClass: 'd-none d-sm-table-cell',thClass: 'd-none d-sm-table-cell'  },
        //        { key: 'groupmember_mobile', label: 'Mobile phone number', class: 'col-3 td-3' },
        { key: 'groupsurveymember_formoption_id', label: 'Form type', sortable: true, class: 'editable select-column' },
        { key: 'actions', label: 'Actions', class: 'ellipsis-button' }
      ],
      fieldsExclude: [
        { key: 'groupsurveymember_survey_code', label: 'Code', tdClass: 'd-none d-sm-table-cell', thClass: 'd-none d-sm-table-cell' },
        { key: 'groupmember_email', label: 'Email', class: 'editable email-column' },
        { key: 'groupsurveymember_formoption_id', label: 'Form type', class: 'editable select-column' },
        { key: 'actions', label: 'Actions', class: 'ellipsis-button' }
      ],
      original_member_data: {}, // To store the original data before editing
      numberSelectedMembersWithEmail: 0,
      canExcludeMembers: true
    }
  },
  computed: {
    plainTextInvitation () {
      return (this.survey.survey_invitation_text || '')
        .replace(/<\/p>/g, '\n\n')
        .replace(/<br\s*\/?>/g, '\n')
        .replace(/<[^>]+>/g, '')
        .replace(/&nbsp;/g, ' ')
    },
    currentcodelink: function () {
      return this.currentlink + '\n\n' + this.currentcode + '\n\n' + this.plainTextInvitation
    },
    includedGroupMembers () {
      return this.groupmembers_grid.filter(member => Number(member.included_in_survey) === 1)
    },
    excludedGroupMembers () {
      return this.groupmembers_grid.filter(member => Number(member.included_in_survey) === 0)
    },
    completedGroupMembers () {
      return this.includedGroupMembers.filter(member => Number(member.groupsurveymember_status_id) === 4)
    },
    normvalues_country_name: {
      get () {
        let normvalues_country_name = ''
        for (let i = 0; i < this.normvalues_countries.length; i++) {
          if (Number(this.normvalues_countries[i].country_id) === Number(this.survey_normvalues_id)) {
            normvalues_country_name = this.normvalues_countries[i].country_name
          }
        }
        return normvalues_country_name
      },
      set () {
        return true
      }
    },
    language_name: {
      get () {
        const langid = this.survey.survey_language_id
        let language_name = ''
        for (let i = 0; i < this.languages.length; i++) {
          if (Number(this.languages[i].id) === Number(langid)) {
            language_name = this.languages[i].language_name
          }
        }
        // var language_name = this.languages[this.$store.state.currentsurvey.survey_language_id];
        return language_name
      },
      set () {
        return true
      }
    },
    anon: function () {
      return this.$store.state.anon
    },
    membersselected () {
      return this.$store.state.selectedsurveymembers || []
    },
    completecount: {
      get () {
        return this.$store.state.currentsurvey.completecount
      },
      set () {
        return true
      }
    },
    membercount: {
      get () {
        return this.$store.state.currentsurvey.membercount
      },
      set () {
        return true
      }
    },
    formoptions: {
      get () {
        const items = this.$store.state.formoptions
        const formoptions = []
        for (let i = 0; i < items.length; i++) {
          const option = {
            value: items[i].id,
            text: items[i].formoption_name
          }
          formoptions.push(option)
        }
        return formoptions
      }
    },
    surveymembers_reload: function () {
      return this.$store.state.surveymembers_reload
    },
    survey_status: {
      get () {
        let status = 'launched'
        if (Number(this.survey_status_id) === 1) {
          status = 'draft'
        }
        if (Number(this.survey_status_id) === 2 && this.survey_start_date && this.survey_start_time) {
          const scheduledAt = new Date(`${this.survey_start_date}T${this.survey_start_time}:00`)
          if (scheduledAt > new Date()) {
            status = 'scheduled'
          }
        }
        return status
      },
      set () {
        return true
      }
    },
    survey_id: {
      get () {
        return this.$store.state.currentsurvey.id
      },
      set () {
        return true
      }
    },
    reminder_date_min: {
      get () {
        if (!this.survey_start_date) { return null }
        let newvalue = new Date(Date.parse(this.survey_start_date))
        newvalue.setDate(newvalue.getDate() + 1)
        const thisdate = new Date()
        if (thisdate > newvalue) {
          newvalue = thisdate
          newvalue.setDate(newvalue.getDate() + 1)
        }
        return newvalue
      },
      set () {
        return true
      }
    },
    canremind: {
      get () {
        if (!this.survey_end_date) { return false }
        const nowvalue = new Date().toISOString().split('T')[0]
        const endvalue = new Date(Date.parse(this.survey_end_date)).toISOString().split('T')[0]
        return endvalue > nowvalue
      },
      set () {
        return true
      }
    }
  },
  watch: {
    surveymembers_reload: function () {
      this.surveymembers_get()
    }
  },
  created () {
    this.survey_get()
    this.surveymembers_get()
    this.group_get()
    this.languages_get()
    this.normvalues_countries_get()
    this.surveytypes_get()
    this.formoptions_get()
    this.labels_get()
    this.screentexts_get()
    this.addonsurveys_get()
    this.changelanguage(this.survey.survey_language_id)
    this.$store.dispatch('groupsurveymembers_setselected', { surveymembers: [] })
    this.countselected()
  },
  mounted () {
    // Initialize Bootstrap collapse manually
    this.$nextTick(() => {
      const collapseButton = document.getElementById('scrollto')
      const collapseElement = document.getElementById('accordion-1')

      if (collapseButton && collapseElement) {
        collapseButton.addEventListener('click', (event) => {
          event.preventDefault()
          event.stopPropagation()

          if (collapseElement.classList.contains('show')) {
            collapseElement.classList.remove('show')
            collapseButton.classList.add('collapsed')
            collapseButton.setAttribute('aria-expanded', 'false')
          } else {
            collapseElement.classList.add('show')
            collapseButton.classList.remove('collapsed')
            collapseButton.setAttribute('aria-expanded', 'true')
          }
        })
      }
    })
  },
  methods: {
    selectall (selected) {
      this.groupmembers_grid = this.groupmembers_grid.map(member => ({
        ...member,
        selected: this.isIncluded(member) ? selected : member.selected
      }))
      this.countselected()
    },
    onSelectAllChange () {
      this.$nextTick(() => {
        this.selectall(this.selected)
      })
    },
    onRowSelectChange () {
      this.$nextTick(() => {
        this.countselected()
      })
    },
    countselected () {
      const selectedsurveymembers = []
      let numbersel = 0
      for (let i = 0; i < this.groupmembers_grid.length; i++) {
        if (this.isIncluded(this.groupmembers_grid[i])) {
          if (this.groupmembers_grid[i].selected) {
            selectedsurveymembers.push(this.groupmembers_grid[i].id)
            numbersel++
          }
        }
      }
      this.selectedCount = numbersel
      this.selected = numbersel > 0 && numbersel === this.includedGroupMembers.length
      this.numberSelectedMembersWithEmail = this.groupmembers_grid.filter(
        member =>
          !!(member.groupmember_email && String(member.groupmember_email).trim()) &&
          Number(member.groupsurveymember_formoption_id) === 1 &&
          member.selected
      ).length
      this.$store.dispatch('groupsurveymembers_setselected', { surveymembers: selectedsurveymembers })
    },
    toggleEditable () {
      this.editable = !this.editable
      const rows = document.querySelectorAll('.table-alternative tr')
      rows.forEach(row => {
        if (this.editable) {
          row.classList.add('editable-row')
        } else {
          row.classList.remove('editable-row')
          this.saveform()
        }
      })
    },
    getlink (id) {
      this.$store.dispatch('groupsurveymember_fetch', { id })
        .then(resp => {
          const data = resp?.data?.result ? resp.data.result : resp.data
          const token = data?.groupsurveymember_token || ''
          const code = data?.groupsurveymember_survey_code || ''
          this.currentlink = token ? `${config.front_url}/gdqquestionnaire/${token}` : ''
          this.currentcode = code
          this.showMemberLink = true
        })
    },
    printform (id) {
      this.$store.dispatch('paperform_fetch', { surveymember_id: id })
        .then((resp) => {
          const paperformsurl = config.report_url + '/gdq_paperforms.php?i=' + resp.data.id
          window.open(paperformsurl)
        })
    },
    rowClass (item) {
      return item.editable ? 'editable-row' : ''
    },
    formoptions_get: function () {
      this.$store.dispatch('formoptions_fetch')
    },
    getFormOptionText (id) {
      const option = this.formoptions.find(option => Number(option.value) === Number(id))
      return option ? option.text : ''
    },
    isIncluded (item) {
      return item && Number(item.included_in_survey) === 1
    },
    statusBadgeClass (item) {
      console.log({status: item.groupsurveymember_status_id})
      if (!item) return 'badge bg-light text-dark'
      const status = Number(item.groupsurveymember_status_id)
      console.log({status})
      // Handle case where status might be NaN due to conversion issues
      if (isNaN(status)) return 'status-tag bg-light border-none text-dark'
      if (status === 1) return 'status-tag bg-danger border-none text-dark'
      if (status === 2) return 'status-tag bg-light border-bottom text-dark'
      if (status === 3) return 'status-tag bg-secondary text-dark'
      if (status === 4) return 'status-tag bg-primary text-dark'
      return 'status-tag bg-light border text-dark'
    },
    statusLabel (item) {
      if (!item) return ''
      const status = Number(item.groupsurveymember_status_id)
      const formOption = Number(item.groupsurveymember_formoption_id)
      if (status === 1) {
        if (formOption === 1) return 'Not sent'
        if (formOption === 2) return 'Not printed'
        return 'Manual send'
      }
      if (status === 2) {
        if (formOption === 1) return 'Sent'
        if (formOption === 2) return 'Printed'
        return 'Manual send'
      }
      if (status === 3) return `Started (${item.gdq_answers})`
      if (status === 4) return `Completed (${item.gdq_answers})`
      return ''
    },
    editGroupMember (id) {
      for (let i = 0; i < this.groupmembers_grid.length; i++) {
        if (Number(this.groupmembers_grid[i].id) === Number(id)) {
          if (this.groupmembers_grid[i].editable) {
            this.groupmembers_grid[i].editable = false
          } else {
            // Cancel edit for all other rows
            this.groupmembers_grid.forEach((member, index) => {
              if (index !== i) {
                this.cancelEdit(member.id)
              }
            })
            this.groupmembers_grid[i].editable = true
            this.original_member_data[id] = JSON.parse(JSON.stringify(this.groupmembers_grid[i]))
          }
        }
      }
    },
    cancelEdit (id) {
      const index = this.groupmembers_grid.findIndex(member => Number(member.id) === Number(id))
      if (index !== -1 && this.original_member_data[id]) {
        this.groupmembers_grid[index].groupsurveymember_survey_code = this.original_member_data[id].groupsurveymember_survey_code
        this.groupmembers_grid[index].groupmember_email = this.original_member_data[id].groupmember_email
        this.groupmembers_grid[index].groupmember_mobile = this.original_member_data[id].groupmember_mobile
        this.groupmembers_grid[index].groupmember_formoption_id = this.original_member_data[id].groupmember_formoption_id
        this.groupmembers_grid[index].groupsurveymember_formoption_id = this.original_member_data[id].groupsurveymember_formoption_id
        this.groupmembers_grid[index].groupmember_status_id = this.original_member_data[id].groupmember_status_id
        this.groupmembers_grid[index].included_in_survey = this.original_member_data[id].included_in_survey
        this.groupmembers_grid[index].editable = false
        delete this.original_member_data[id]
      }
    },
    saveEdit (id) {
      const index = this.groupmembers_grid.findIndex(member => Number(member.id) === Number(id))
      if (index !== -1) {
        const member = this.groupmembers_grid[index]
        const original = this.original_member_data[id]
        const originalEmail = original.groupmember_email || ''
        if (this.validateEditedMember(member)) {
          if (member.groupmember_email != originalEmail) {
            member.groupsurveymember_status_id = 1
            this.groupmembers_grid[index].groupsurveymember_status_id = 1
          }
          member.editable = false
          delete this.original_member_data[id]
          this.updatesurvey(false)
        } else {
          member.groupmember_email_valid = 'invalid'
          const mail = member.groupmember_email
          member.groupmember_email = ''
          member.groupmember_email = mail
        }
      }
    },
    validateEditedMember (member) {
      if (member.groupsurveymember_formoption_id != 1) {
        member.groupmember_email_valid = null
        member.groupmember_email = ''
        return true
      } else {
        return this.parse_email(member.groupmember_email)
      }
    },

    parse_email (email) {
      // eslint-disable-next-line
      const pattern = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      return pattern.test(email.toLowerCase())
    },

    languages_get () {
      this.$store.dispatch('languages_fetch')
        .then((res) => {
          this.languages = res.data.result
        })
    },

    surveytype_get () {
      this.$store.dispatch('record_read', { table_name: 'g_surveytypes', id: this.survey_type_id })
        .then((resp) => {
          this.surveytype_name = resp.data.surveytype_name
        })
    },

    addonsurveys_get () {
      this.$store.dispatch('record_readlist', { table_name: 'g_addon_surveys', orderby: 'survey_name' })
        .then((resp) => {
          const result = resp.data.result
          const options = new Array()
          options.push({ value: 0, text: '-- No add-on survey --' })
          for (let i = 0; i < result.length; i++) {
            options.push(
              { value: result[i].id, text: result[i].survey_name }
            )
          }
          this.addonsurveys = options
        })
    },
    language_options_get () {
      const survey_type_id = this.survey_type_id
      const filter = {}
      switch (survey_type_id) {
        case '1':
          filter.language_enabled = {
            operator: '=',
            value: '1'
          }
          break
        case '2':
          filter.language_enabled_gdq_short = {
            operator: '=',
            value: '1'
          }
          break
        case '3':
          filter.language_enabled_osc = {
            operator: '=',
            value: '1'
          }
          break
        default:
          filter.language_enabled = {
            operator: '=',
            value: '1'
          }
      }
      this.$store.dispatch('records_readall', { table_name: 'g_languages', filter, orderby: 'language_name' })
        .then((resp) => {
          this.language_options = []
          for (let i = 0; i < resp.data.result.length; i++) {
            this.language_options.push({
              value: resp.data.result[i].id,
              text: resp.data.result[i].language_name
            })
          }
        })
      return true
    },
    openquestionnaire (id) {
      this.$store.dispatch('groupsurveymember_fetch', { id })
        .then((resp) => {
          const data = resp?.data?.result ? resp.data.result : resp.data
          const token = data?.groupsurveymember_token
          const code = data?.groupsurveymember_survey_code
          this.$store.dispatch('questionnaire_setadmin', { admin: true })
            .then(() => {
              this.$store.dispatch('questionnaire_setadmin_code', { code })
                .then(() => {
                  window.open('/gdqquestionnaire/' + token)
                })
            })
        })
    },
    admin_excluded: function () {
      this.excludablequestions_get()
      this.administer_excluded = !this.administer_excluded
      if (this.administer_excluded) {
        this.administer_mailtext = false
        this.administer_reimport = false
        this.administer_addon = false
      }
    },
    excludablequestions_get () {
      this.excludablequestions = []
      this.$store.dispatch('excludedquestions_fetch', { survey_id: this.survey_id })
        .then((resp) => {
          this.excludablequestions = resp.data.questions
          for (let i = 0; i < resp.data.questions.length; i++) {
            if (Number(resp.data.questions[i].excluded) === 1) {
              this.excludedquestions.push(resp.data.questions[i].generalquestion_id)
            }
          }
        })
    },
    async admin_excluded_save () {
      await this.$store.dispatch('excludedquestions_save', { survey_id: this.survey_id, questions: this.excludedquestions })
        .then(() => {
          this.administer_excluded = false
        })
    },
    admin_mailtext: function () {
      this.administer_mailtext = !this.administer_mailtext
      if (this.administer_mailtext) {
        this.administer_excluded = false
        this.administer_reimport = false
        this.administer_addon = false
      }
    },
    admin_addon: function () {
      this.administer_addon = !this.administer_addon
      if (this.administer_mailtext) {
        this.administer_excluded = false
        this.administer_reimport = false
        this.administer_mailtext = false
      }
    },
    admin_mailtext_save () {
      const data = {
        id: this.$store.state.currentsurvey.id,
        survey_invitation_subject: this.survey_invitation_subject,
        survey_invitation_consultant_text: this.survey_invitation_consultant_text
      }
      this.$store.dispatch('groupsurvey_save', { survey: data }).then(() => {
        this.survey_get()
        this.administer_mailtext = !this.administer_mailtext
      })
    },
    admin_addon_save () {
      return true
    },
    admin_reimport: function () {
      this.reimporting = -1
      this.administer_reimport = !this.administer_reimport
      if (this.administer_reimport) {
        this.administer_excluded = false
        this.administer_mailtext = false
        this.administer_addon = false
      }
    },
    reimport: async function () {
      const surveymembers = this.$store.state.currentsurveymembers
      this.reimported = []
      this.reimportstatus = 0
      for (let i = 0; i < surveymembers.length; i++) {
        this.reimporting = i + 1
        const m = surveymembers[i]
        const data = { surveymember_id: m.id }
        await this.$store.dispatch('legacyanswers_get', data)
          .then(resp => {
            if (resp.data.old_id === '0') {
              this.reimportstatus = 2
            }
          }
          )
      }
      if (Number(this.reimportstatus) === 0) { this.reimportstatus = 1 }
      this.survey_get()
      this.reimporting = -1
    },
    screentexts_get: function () {
      const screentexts = []
      const filter = {
        screen_id: {
          operator: '=',
          value: this.screen_id
        }
      }
      this.$store
        .dispatch('systemscreenelements_fetch', { filter })
        .then(resp => {
          const dbtexts = resp.data.result
          for (let i = 0; i < dbtexts.length; i++) {
            const screentext = {
              id: dbtexts[i].id,
              element_text: dbtexts[i].element_text,
              element_bubbletext: dbtexts[i].element_bubbletext
            }
            screentexts[dbtexts[i].id] = screentext
          }
          this.screentexts = screentexts
        })
    },
    print_paperforms () {
      this.$store
        .dispatch('paperforms_fetch', {
          survey_id: this.$store.state.currentsurvey.id
        })
        .then(resp => {
          const paperformsurl =
            config.report_url + '/gdq_paperforms.php?i=' + resp.data.id
          window.open(paperformsurl)
        })
    },
    groupsurvey_report () {
      this.$router.push('/group/survey/report')
    },
    async groupsurvey_reopen () {
      const data = {
        id: this.$store.state.currentsurvey.id,
        survey_status_id: 3
      }
      this.formchanged = false
      this.survey.survey_status_id = 3
      await this.$store.dispatch('groupsurvey_save', { survey: data }).then(() => {
        this.survey_get()
      })
    },
    groupsurvey_close_ask () {
      this.confirmAnonymize = false
      this.wanttoclose = true
    },
    groupsurvey_close_cancel () {
      this.wanttoclose = false
    },
    async groupsurvey_close () {
      if (Number(this.anonymize_survey_on_close) === 1) {
        await this.$store.dispatch('groupsurvey_anonymize', { survey_id: this.$store.state.currentsurvey.id })
      }
      this.$store
        .dispatch('groupsurvey_close', {
          survey: this.$store.state.currentsurvey
        })
        .then(() => {
          this.wanttoclose = false
          this.survey_get()
          this.$store.dispatch('surveymembers_setreload', { reload: 1 })
        })
    },
    cetdate_get: async function () {
      const resp = await this.$store.dispatch('serverdate_fetch')
      const thedate = resp.data
      console.log
      return thedate
    },
    async groupsurvey_launch () {
      this.survey_status_id = 3
      const mailrecipients = []
      for (let i = 0; i < this.$store.state.currentsurveymembers.length; i++) {
        if (Number(this.$store.state.currentsurveymembers[i].groupsurveymember_formoption_id) === 1) {
          mailrecipients.push(this.$store.state.currentsurveymembers[i])
        }
      }
      const payload = {
        survey: this.$store.state.currentsurvey,
        surveymembers: mailrecipients,
        mail_subject: this.$store.state.currentsurvey.survey_invitation_subject,
        mail_text: this.$store.state.currentsurvey.survey_invitation_text,
        mail_consultant_text: this.mail_consultant_text,
        mail_cc: this.$store.state.currentsurvey.survey_invitation_cc,
        mail_type: 'Invitation'
      }
      await this.$store.dispatch('groupsurvey_mail', payload)
      const serverdate = await this.cetdate_get()
      console.log('Server date:', serverdate)
      const data = {
        id: this.$store.state.currentsurvey.id,
        survey_status_id: 3,
        survey_start_date: serverdate
      }
      console.log('Saving with data:', { data })
      this.formchanged = false
      await this.$store.dispatch('groupsurvey_save', { survey: data })
      const dbsurvey = await this.survey_get()
      // console.log({ dbsurvey })
      this.survey = dbsurvey
      await this.$store.dispatch('surveymembers_setreload', { reload: 1 })
    },
    checkreadyforlaunch () {
      let ready = true
      // No members - cannot launch
      if (Number(this.membercount) === 0) {
        ready = false
      }
      // End date - cannot launch
      if (
        this.survey_end_date === null ||
        this.survey_end_date === '' ||
        this.survey_end_date < this.survey_start_date
      ) {
        ready = false
      }
      // Here I am not sure, I don't think we should set the start date
      if (
        (this.survey_start_date === null || this.survey_start_date === '') &&
        ready === true
      ) {
        ready = false
      }
      if (ready) {
        const data = {
          id: this.$store.state.currentsurvey.id,
          survey_status_id: 2
        }
        this.formchanged = false
        this.$store.dispatch('groupsurvey_save', { survey: data }).then(() => {
          this.survey_get()
        })
      }
    },
    changelanguage (survey_language_id) {
      // Copy the mail text for the new language
      this.$store
        .dispatch('groupsurvey_changelanguage', {
          survey_id: this.$store.state.currentsurvey.id,
          survey_language_id
        })
        .then(() => {
          // Then save the molevitt
          this.savefield('survey_language_id', survey_language_id)
        })
    },
    savefield (fieldname, value) {
      if (!this.loading) {
        if (fieldname === 'survey_reminder') {
          if (!value) {
            value = 0
          }
        }
        if (fieldname.indexOf('_date') != -1) {
          value = this.format_date(value)
        }
        if (fieldname.indexOf('_time') != -1) {
          if (value != null && value != '') {
            value = this.format_time(value)
          } else {
            value = '00:00'
          }
        }
        if (this.validateform()) {
          const data = {
            id: this.$store.state.currentsurvey.id,
            [fieldname]: value
          }
          this.formchanged = false
          this.$store
            .dispatch('groupsurvey_save', { survey: data })
            .then(() => {
              if (Number(this.survey_status_id) === 1) {
                this.checkreadyforlaunch()
              }
              if (fieldname === 'survey_start_date') {
                this.surveychanged = !this.surveychanged
              }
            })
            .then(() => {
              this.survey_get()
            })
        }
      }
    },
    validateform () {
      let result = true
      if (this.survey_name === '') {
        this.survey_name_valid = false
        result = false
      } else {
        this.survey_name_valid = true
      }
      if (Number(this.survey_normvalues_id) === 0) {
        this.survey_normvalues_id_valid = false
        result = false
      }
      return result
    },
    saveform () {
      if (!this.saving) {
        this.saving = true
        this.formchanged = false
        const data = {
          id: this.$store.state.currentsurvey.id,
          survey_name: this.survey.survey_name,
          survey_invoice_reference: this.survey_invoice_reference,
          survey_normvalues_id: this.survey_normvalues_id,
          survey_language_id: this.survey.survey_language_id,
          survey_start_date: this.format_date(this.survey_start_date),
          survey_start_time: this.format_time(this.survey_start_time),
          survey_end_date: this.format_date(this.survey_end_date),
          survey_reminder_date: this.format_date(this.survey_reminder_date),
          survey_reminder_time: this.format_time(this.survey_reminder_time),
          survey_reminder: this.survey_reminder,
          survey_reminder_email: this.survey_reminder_email,
          labels: [],
          addinglabel: 0,
          addinglabeltext: 0
        }
        this.$store.dispatch('groupsurvey_save', { survey: data })
          .then(() => {
            this.surveychanged = !this.surveychanged
            this.survey_get()
            this.saving = false
          })
      }
    },
    async updatesurvey (adding) {
      this.addingMember = true
      try {
        const newmember = {
          newid: 0,
          id: this.nextid,
          groupmember_email: this.groupmember_email,
          groupmember_mobile: this.groupmember_mobile,
          groupmember_name: this.groupmember_name,
          groupmember_formoption_id: this.groupmember_formoption_id,
          groupsurveymember_formoption_id: this.groupmember_formoption_id,
          included_in_survey: 1
        }
        if (newmember != null && adding) {
          this.nextid++
          this.cleargroupmember()
          this.groupmembers_grid.push(newmember)
          const data = {
            group_id: this.$store.state.currentgroup_id,
            survey_id: this.$store.state.currentsurvey.id,
            groupmembers: this.groupmembers_grid,
            group: this.$store.state.currentgroup,
            survey: this.$store.state.currentsurvey
          }
          this.cleargroupmember()
          this.$store.dispatch('addingsurveymember_set', {
            addingsurveymember: true
          })
          await this.$store.dispatch('groupsurveymembers_create', data)
          this.surveymembers_get()
        } else {
          const savemembers = []
          for (let i = 0; i < this.groupmembers_grid.length; i++) {
            const member = {
              newid: this.groupmembers_grid[i].id,
              id: this.groupmembers_grid[i].groupmember_id,
              groupmember_email: this.groupmembers_grid[i].groupmember_email,
              groupmember_name: this.groupmembers_grid[i].groupmember_name,
              groupmember_mobile: this.groupmembers_grid[i].groupmember_mobile,
              groupsurveymember_formoption_id: this.groupmembers_grid[i].groupsurveymember_formoption_id,
              groupsurveymember_status_id: this.groupmembers_grid[i].groupsurveymember_status_id,
              included_in_survey: this.groupmembers_grid[i].included_in_survey
            }
            savemembers.push(member)
          }
          const data = {
            group_id: this.$store.state.currentgroup_id,
            survey_id: this.$store.state.currentsurvey.id,
            groupmembers: savemembers,
            group: this.$store.state.currentgroup,
            survey: this.$store.state.currentsurvey
          }
          this.cleargroupmember()
          await this.$store.dispatch('groupsurveymembers_create', data)
          this.surveymembers_get()
        }
      } finally {
        this.addingMember = false
      }
    },
    clearform () {
      this.formchanged = false
      this.survey_get()
    },
    setformchanged () {
      if (!this.loading) {
        this.formchanged = true
      }
      return true
    },
    // Convert a CET naive date+time to a UTC Date object.
    cet_to_date (datePart, timePart) {
      if (!datePart || !timePart || String(timePart).length !== 5) return null
      const attempt = new Date(`${datePart}T${timePart}:00+01:00`)
      const check = attempt.toLocaleString('sv-SE', { timeZone: 'Europe/Berlin' }).split(' ')[1].substring(0, 5)
      return check === timePart ? attempt : new Date(`${datePart}T${timePart}:00+02:00`)
    },
    // Convert local date (YYYY-MM-DD) + local time (HH:MM) to CET {date, time}.
    local_to_cet (localDate, localTime) {
      if (!localDate || !localTime) return { date: localDate || '', time: localTime || '' }
      const d = new Date(`${String(localDate).substring(0, 10)}T${localTime}:00`)
      return {
        date: d.toLocaleDateString('sv-SE', { timeZone: 'Europe/Berlin' }),
        time: d.toLocaleTimeString('sv-SE', { timeZone: 'Europe/Berlin', hour: '2-digit', minute: '2-digit' })
      }
    },
    saveEndDate (value) {
      if (this.loading) return
      const dateStr = this.format_date(value)
      const data = {
        id: this.$store.state.currentsurvey.id,
        survey_end_date: dateStr
      }
      this.$store.dispatch('groupsurvey_save', { survey: data })
        .then(() => {
          this.surveychanged = !this.surveychanged
          this.survey_get()
        })
    },
    saveStartDateTime () {
      if (this.loading) return
      const cet = this.local_to_cet(this.survey_start_date, this.survey_start_time)
      const data = {
        id: this.$store.state.currentsurvey.id,
        survey_start_date: cet.date,
        survey_start_time: cet.time
      }
      this.$store.dispatch('groupsurvey_save', { survey: data })
        .then(() => {
          this.surveychanged = !this.surveychanged
          this.survey_get()
        })
    },
    onStartTimeChange () {
      this.checkstartdate()
      this.saveStartDateTime()
    },
    checkstartdate () {
      if (this.loading === true) { return true }
      this.showtimeadjust = false
      if (!this.survey_start_date || !this.survey_start_time) return
      const surveydate = new Date(`${this.survey_start_date}T${this.survey_start_time}:00`)
      if (surveydate < new Date()) {
        this.showtimeadjust = true
      }
    },
    togglegroupmember (id) {
      for (let i = 0; i < this.groupmembers_grid.length; i++) {
        if (Number(this.groupmembers_grid[i].id) === Number(id)) {
          this.groupmembers_grid[i].included_in_survey = this.isIncluded(this.groupmembers_grid[i]) ? 0 : 1
          if (!this.isIncluded(this.groupmembers_grid[i])) {
            this.groupmembers_grid[i].selected = false
          }
          this.updatesurvey(false)
        }
      }
    },
    cleargroupmember () {
      this.groupmember_name = ''
      this.groupmember_email = ''
      this.groupmember_mobile = ''
      this.groupmember_name_valid = null
      this.groupmember_email_valid = null
      this.groupmember_mobile_valid = null
    },
    validategroupmember () {
      let formok = true
      this.groupmember_name_valid = null
      this.groupmember_email_valid = null
      this.groupmember_mobile_valid = null
      // Online form
      if (Number(this.groupmember_formoption_id) === 1) {
        if (this.groupmember_email === '') {
          this.groupmember_email_valid = 'invalid'
          formok = false
        }
        // eslint-disable-next-line
        // if (/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(this.groupmember_email) == false) {
        if (!this.parse_email(this.groupmember_email)) {
          this.groupmember_email_valid = 'invalid'
          formok = false
        }
      }
      return formok
    },
    addgroupmember () {
      if (this.validategroupmember()) {
        this.updatesurvey(true)
      }
    },
    toggleaddmember () {
      this.cleargroupmember()
      this.addmember = !this.addmember
      this.contactmembers = false
      this.sendinvitations = false
    },
    togglecontactmembers () {
      this.contactmembers = !this.contactmembers
      this.sendinvitations = false
      if (this.addmember) {
        this.cleargroupmember()
      }
      this.addmember = false
    },
    togglesendinvitations () {
      this.invitationsSent = 0
      this.sendinvitations = !this.sendinvitations
      this.contactmembers = false
      if (this.addmember) {
        this.cleargroupmember()
      }
      this.addmember = false
    },
    toggleSelected (id, scrollto, type) {
      for (let i = 0; i < this.groupmembers_grid.length; i++) {
        if (Number(this.groupmembers_grid[i].id) === Number(id)) {
          this.groupmembers_grid[i].selected = true
        } else {
          this.groupmembers_grid[i].selected = false
        }
      }
      if (type === 'invite') {
        this.sendinvitations = true
        this.contactmembers = false
        if (this.addmember) {
          this.cleargroupmember()
        }
        this.addmember = false
      } else if (type === 'contact') {
        this.contactmembers = true
        this.sendinvitations = false
        if (this.addmember) {
          this.cleargroupmember()
        }
        this.addmember = false
      }
      this.countselected()

      this.$nextTick(() => {
        setTimeout(() => {
          const element = document.getElementById(scrollto)
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' })
          }
        }, 100)
      })
    },
    sendemails (type) {
      const mailrecipients = []
      let send_mail_subject = ''
      let send_mail_text = ''
      let send_mail_consultant_text = ''
      let send_mail_cc = ''
      if (type === 'Invitation') {
        send_mail_subject = this.survey_invitation_subject
        send_mail_text = this.survey_invitation_text
        send_mail_consultant_text = this.survey_invitation_consultant_text
        send_mail_cc = this.survey_invitation_cc
      }
      if (type === 'Other') {
        send_mail_subject = this.mail_subject
        send_mail_text = this.mail_consultant_text
        send_mail_consultant_text = this.mail_consultant_text
        send_mail_cc = ''
      }
      for (let i = 0; i < this.$store.state.currentsurveymembers.length; i++) {
        if (this.membersselected.includes(this.$store.state.currentsurveymembers[i].id)) {
          if (Number(this.$store.state.currentsurveymembers[i].groupsurveymember_formoption_id) === 1) {
            mailrecipients.push(this.$store.state.currentsurveymembers[i])
          }
        }
      }
      const payload = {
        survey: this.$store.state.currentsurvey,
        surveymembers: mailrecipients,
        mail_subject: send_mail_subject,
        mail_text: send_mail_text,
        mail_consultant_text: send_mail_consultant_text,
        mail_cc: send_mail_cc,
        mail_type: type
      }
      this.contactmembers = false
      this.sendinvitations = false
      if (type === 'Invitation') {
        this.invitationsSent = mailrecipients.length
        this.selectall(false)
      }
      this.$store.dispatch('groupsurvey_mail', payload)
      // -------------------------------------------------------------------
      // Since the mail is done "detached", we need some timing thingy here
      // -------------------------------------------------------------------
      setTimeout(
        function () {
          this.survey_get()
          this.$store.dispatch('surveymembers_setreload', { reload: 1 })
        }.bind(this),
        1500
      )
    },
    surveymembers_get () {
      this.$store.dispatch('groupsurveyandgroupmembers_fetch', { survey_id: this.$store.state.currentsurvey.id })
        .then((resp) => {
          const groupsurveymembers = new Array()
          for (let i = 0; i < resp.data.result.length; i++) {
          const groupsurveymember_formoption_id = resp.data.result[i].groupsurveymember_formoption_id
          let groupsurveymember_formoption_name = 'None'
          for (let j = 0; j < this.formoptions.length; j++) {
            if (Number(resp.data.result[i].groupsurveymember_formoption_id) === Number(this.formoptions[j].value)) {
              groupsurveymember_formoption_name = this.formoptions[j].text
              }
            }
            groupsurveymembers.push({
              id: resp.data.result[i].id,
              groupmember_id: resp.data.result[i].groupmember_id,
              groupsurveymember_survey_code: resp.data.result[i].groupsurveymember_survey_code,
              groupmember_name: resp.data.result[i].groupmember_name,
              groupmember_email: resp.data.result[i].groupmember_email,
              groupmember_mobile: resp.data.result[i].groupmember_mobile,
              groupsurveymember_formoption_id: Number(groupsurveymember_formoption_id),
              groupsurveymember_formoption_name,
              groupsurveymember_status_id: resp.data.result[i].groupsurveymember_status_id,
              gdq_answers: resp.data.result[i].gdq_answers,
              included_in_survey: Number(resp.data.result[i].included_in_survey) === 1 ? 1 : 0,
              selected: false,
              editable: false
            })
          }
          this.groupmembers_grid = groupsurveymembers
        })
    },
    survey_get: async function () {
      this.loading = true
      const res = await this.$store
        .dispatch('groupsurvey_fetch', { id: this.survey_id })
        .then((res) => {
          this.$store.commit('survey_set', res.data)
          this.group_name = this.$store.state.currentgroup.group_name
          this.survey_name = res.data.survey_name || ''
          this.survey_start_date = null
          this.survey_start_time = null
          if (res.data.survey_start_date > '2000-01-01') {
            const d = this.cet_to_date(res.data.survey_start_date, res.data.survey_start_time)
            if (d) {
              this.survey_start_date = d.toLocaleDateString('sv-SE')
              this.survey_start_time = d.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })
            }
          }
          this.survey_end_date = null
          if (res.data.survey_end_date > '2000-01-01') {
            this.survey_end_date =
              this.format_date(
                res.data.survey_end_date
              ) + 'T00:00:00.00Z'
            this.survey_end_time = res.data.survey_end_time || '23:59'
          } else {
            this.survey_end_time = null
          }
          this.survey_reminder = res.data.survey_reminder
          if (
            res.data.survey_reminder_date > '2000-01-01'
          ) {
            this.survey_reminder_date =
              this.format_date(
                res.data.survey_reminder_date
              ) + 'T00:00:00.00Z'
            this.survey_reminder_time = res.data.survey_reminder_time
          }
          this.survey_reminder_email = res.data.survey_reminder_email
          this.survey_language_id = res.data.survey_language_id
          this.survey_normvalues_id = res.data.survey_normvalues_id
          this.survey_type_id = Number(res.data.survey_type_id) || res.data.survey_type_id
          this.survey_status_id = Number(res.data.survey_status_id) || 0
          // Keep survey object in sync and numeric so comparisons are reliable
          this.survey.survey_status_id = this.survey_status_id
          this.survey_invoice_reference = res.data.survey_invoice_reference
          this.survey_invitation_subject = res.data.survey_invitation_subject
          this.survey_invitation_text = res.data.survey_invitation_text
          this.mail_consultant_text = res.data.survey_invitation_consultant_text
          this.survey_invitation_cc = res.data.survey_invitation_cc
          this.spotify = res.data.spotify
          this.addon_survey = res.data.addon_survey
          this.redirect_if_incomplete()

          const members = this.$store.state.currentsurveymembers
          for (let i = 0; i < members.length; i++) {
            if (Number(members[i].groupsurveymember_formoption_id) === 1) {
              this.has_mail = 1
            }
            if (Number(members[i].groupsurveymember_formoption_id) === 2) {
              this.has_paper = 1
            }
          }
          this.$store.dispatch('record_read', { table_name: 'g_surveytypes', id: this.survey_type_id })
            .then((resp) => {
              this.surveytype_name = resp.data.surveytype_name
            })
            .then(() => {
              this.language_options_get()
            })
            .then(() => {
              this.normvalues_countries_get()
            })
          // .then(() => {
          //   if (this.spotify == 1) {
          //     this.changelanguage(this.survey_language_id);
          //   }
          // })
          const filter = {
            survey_id: {
              operator: '=',
              value: this.survey_id
            }
          }
          this.$store.dispatch('records_readall', { table_name: 'g_gdqreports', filter })
            .then((resp) => {
              if (resp.data.result.length) {
                this.canExcludeMembers = false
              }
            })
          this.loading = false
          return res.data
        })
      // console.log({ surveygetres: res })
      return res
    },
    group_get: function () {
      this.$store
        .dispatch('group_fetch', { id: this.$store.state.currentgroup_id })
        .then(() => {
          this.group_name = this.$store.state.currentgroup.group_name
      })
    },
    redirect_if_incomplete () {
      const missingName = !this.survey_name || this.survey_name.trim() === ''
      const missingStart = !this.survey_start_date || this.survey_start_date.startsWith('1970')
      if (missingName || missingStart) {
        const gid = this.$store.state.currentgroup_id || (this.survey && this.survey.group_id) || null
        if (gid) {
          this.$store.dispatch('group_setcurrent', gid)
        }
        this.$router.replace('/create/group/survey/details')
      }
    },

    normvalues_countries_get: function () {
      const survey_type_id = this.survey_type_id
      this.$store.dispatch('normvalues_countries_fetch')
        .then((resp) => {
          const options = []
          options.push({ value: 0, text: 'Please select norm values' })
          for (let i = 0; i < resp.data.length; i++) {
            if (Number(resp.data[i].country_id) === 999 || Number(survey_type_id) === 1) {
              const option = {
                value: resp.data[i].country_id,
                text: resp.data[i].country_name
              }
              options.push(option)
            }
          }
          this.norm_values_options = options
        })
    },
    surveytypes_get: function () {
      this.$store.dispatch('surveytypes_fetch')
    },
    //= ====
    // LABELS
    //= ====
    labels_get () {
      this.$store
        .dispatch('groupsurveylabels_fetch', { survey_id: this.survey_id })
        .then(resp => {
          this.labels = resp.data
        })
    },
    saveaddlabel () {
      if (this.addinglabeltext != '') {
        const savelabel = {
          survey_id: this.addinglabel,
          label: this.addinglabeltext
        }
        this.$store
          .dispatch('groupsurveylabel_add', { label: savelabel })
          .then(() => {
            this.labels_get()
          })
      }
      this.addinglabel = 0
      this.addinglabeltext = ''
    },
    addlabel (id) {
      this.addinglabel = id
      this.addinglabeltext = ''
      this.$nextTick(() => this.$refs.addinglabel.focus())
    },
    removelabel (groupsurveylabel_id) {
      this.$store
        .dispatch('groupsurveylabel_delete', { id: groupsurveylabel_id })
        .then(() => {
          this.labels_get()
        })
    },
    //= ===========
    // HELPER FUNCTIONS
    //= ===========
    format_date (date) {
      if (!date) return ''
      if (date instanceof Date) {
        return date.toLocaleDateString('sv-SE', { timeZone: 'Europe/Berlin' })
      }
      return String(date).substring(0, 10)
    },
    format_time (date) {
      if (!date) return ''
      if (typeof date === 'string' && date.length === 5) return date
      const d = new Date(date)
      if (isNaN(d.getTime())) return String(date)
      return d.toLocaleTimeString('sv-SE', { timeZone: 'Europe/Berlin', hour: '2-digit', minute: '2-digit' })
    },
    copyText () {
      const editorContent = document.querySelector('#fixed-text')
      const range = document.createRange()
      range.selectNodeContents(editorContent)
      const selection = window.getSelection()
      selection.removeAllRanges()
      selection.addRange(range)
      document.execCommand('copy')
      selection.removeAllRanges()
    }
  }
}
</script>
<style scoped>
.gdq-strike {
  text-decoration: line-through;
}

/* Override Bootstrap dropdown styles to ensure bg-secondary is applied */
.dropdown-menu.bg-secondary {
  background-color: #6c757d !important;
}

.dropdown-menu.bg-secondary .dropdown-item.bg-secondary {
  background-color: #6c757d !important;
  color: white !important;
}

.dropdown-menu.bg-secondary .dropdown-item.bg-secondary:hover {
  background-color: #5a6268 !important;
  color: white !important;
}

.status-tag {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  height: 44px;
  box-sizing: border-box;
  padding: 8px;
  margin: 0;
  line-height: 1.2;
  font-size: inherit;
  font-weight: 400;
  border-radius: 0;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

:deep(td.color-input) {
  padding: 0 !important;
  border: 0 !important;
}

:deep(td.color-input .status-tag) {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  height: 44px;
  box-sizing: border-box;
  padding: 8px;
  margin: 0;
  line-height: 1.2;
  font-size: inherit;
  font-weight: 400;
  border-radius: 0;
  text-align: left;
  border: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

:deep(.actions-table th.select-th input.form-check-input),
:deep(.actions-table td.select-td input.form-check-input) {
  width: 1rem;
  height: 1rem;
  margin: 0;
  padding: 0;
  appearance: none !important;
  -webkit-appearance: none !important;
  opacity: 1 !important;
  position: static !important;
  display: inline-block !important;
  z-index: 1;
  pointer-events: auto;
  background-color: #fff;
  border: 1px solid #adb5bd !important;
  border-radius: 0.25rem;
  box-shadow: none;
  background-repeat: no-repeat;
  background-position: center;
  background-size: 0.65rem 0.65rem;
}

:deep(.actions-table td.select-td input.form-check-input:checked),
:deep(.actions-table th.select-th input.form-check-input:checked) {
  background-color: #007d8f !important;
  border-color: #007d8f !important;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3e%3cpath fill='none' stroke='%23fff' stroke-width='3' d='M5 10l3 3 7-7'/%3e%3c/svg%3e");
}

:deep(.actions-table td.select-td),
:deep(.actions-table th.select-th) {
  width: 16px;
  min-width: 16px;
  padding-left: 0;
  padding-right: 0;
  border: 0;
  text-align: center;
  vertical-align: middle;
}
</style>
